import React, { useState } from 'react';
import { Employee } from '../types/employee';
import { useAlert } from '../contexts/AlertContext';

interface ReturnModalProps {
  isOpen: boolean;
  onClose: () => void;
  employee?: Employee;
  onSubmit: (data: ReturnRequestData) => void;
}

export interface ReturnRequestData {
  returnDate: string;
  memo: string;
}

const ReturnModal: React.FC<ReturnModalProps> = ({ isOpen, onClose, employee, onSubmit }) => {
  const { showToast } = useAlert();
  const [formData, setFormData] = useState<ReturnRequestData>({
    returnDate: new Date().toISOString().split('T')[0], // 기본값을 오늘 날짜로 설정
    memo: ''
  });

  // 유효성 검사 함수
  const validateForm = (): string | null => {
    if (!formData.returnDate) {
      return '복직일을 입력해주세요.';
    }

    const returnDate = new Date(formData.returnDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // 시간 부분 제거

    // 복직일이 과거가 아닌지 확인 (당일 허용)
    if (returnDate < today) {
      return '복직일은 오늘 이후로 입력해주세요.';
    }

    return null;
  };

  const handleSubmit = () => {
    // 유효성 검사 실행
    const validationError = validateForm();

    if (validationError) {
      showToast(validationError, 'warning');
      return;
    }

    onSubmit(formData);
    setFormData({
      returnDate: new Date().toISOString().split('T')[0],
      memo: ''
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center">
      <div 
        className="absolute inset-0 bg-black bg-opacity-40"
        onClick={onClose}
      ></div>
      <div className="w-full max-w-lg bg-white rounded-xl shadow-2xl relative z-50">
        <header className="p-4 border-b border-slate-200">
          <h3 className="font-bold text-lg">복직 등록</h3>
        </header>
        <div className="p-6 space-y-4 text-sm">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block font-medium text-slate-500">이름</label>
              <p className="mt-1 font-semibold">{employee?.name}</p>
            </div>
            <div>
              <label className="block font-medium text-slate-500">사번</label>
              <p className="mt-1 font-semibold">{employee?.empNo}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block font-medium text-slate-500">현재 상태</label>
              <p className="mt-1 font-semibold">
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                  휴직중
                </span>
              </p>
            </div>
            <div>
              <label className="block font-medium text-slate-600 mb-1">
                복직일 <span className="text-red-500">*</span>
              </label>
              <input 
                type="date" 
                value={formData.returnDate}
                min={new Date().toISOString().split('T')[0]} // 오늘 이후만 선택 가능
                onChange={(e) => setFormData({...formData, returnDate: e.target.value})}
                className={`w-full p-2 border rounded-md ${
                  !formData.returnDate 
                    ? 'border-red-300 focus:border-red-500' 
                    : 'border-slate-300 focus:border-indigo-500'
                }`}
              />
              {!formData.returnDate && (
                <p className="text-xs text-red-500 mt-1">복직일을 입력해주세요</p>
              )}
            </div>
          </div>
          <div>
            <label className="block font-medium text-slate-600 mb-1">MEMO</label>
            <textarea 
              rows={3} 
              value={formData.memo}
              onChange={(e) => setFormData({...formData, memo: e.target.value})}
              className="w-full p-2 border border-slate-300 rounded-md"
            ></textarea>
          </div>
        </div>
        <footer className="p-4 bg-slate-50 flex justify-end space-x-2">
          <button 
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50"
          >
            취소
          </button>
          <button 
            onClick={handleSubmit}
            disabled={!formData.returnDate}
            className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
              !formData.returnDate
                ? 'text-slate-400 bg-slate-300 cursor-not-allowed'
                : 'text-white bg-indigo-600 hover:bg-indigo-700'
            }`}
          >
            복직 등록
          </button>
        </footer>
      </div>
    </div>
  );
};

export default ReturnModal;